import { ALBUMS, LIVE_LIBRARIES } from '@disco/constants/FeatureTypes'

import mixpanel from '@disco/api/mixpanel'

import React, { useCallback, useEffect, useMemo } from 'react'
import CopyToClipboard from 'react-clipboard.js'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useParams, useLocation, Outlet } from 'react-router-dom'
import type { ClientLibrary, ClientLibraryId } from 'types/client-library'
import type { Location } from 'react-router-dom'
import type { State } from 'types/redux'
import {
  clientLibraryListGetSingle,
  clientLibraryUpdate,
} from '@disco/state-management/actions/client-library-list-actions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getClientLibrary } from '@disco/state-management/selectors/client-libraries'
import { isClientLibrariesTrial } from '@disco/state-management/selectors/user'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'
import { pushUINotification } from '@disco/utils/notifications'
import { LibraryEasyAccessAlert } from 'components/libraries/libraries-easy-access-alert'
import LibrariesTrackQuotaAlert from 'components/libraries/libraries-track-quota-alert'
import LibrariesUpgradeAlert from 'components/libraries/libraries-upgrade-alert'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import QRCodeButton from 'components/ui/qr-code-button'
import Flex from 'components/ui/flex'
import Lozenge from 'components/ui/lozenge'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import PageLayout from 'components/ui/page-layout'
import Switch from 'components/ui/switch'
import styles from './index.module.css'

const LibrariesLibrary = (): React.ReactNode | React.ReactElement<'div'> => {
  const params = useParams<{ libraryId: string }>()
  const location = useLocation()
  const libraryId = params.libraryId as unknown as ClientLibraryId
  const dispatch = useDispatch()
  const library: ClientLibrary | null | undefined = useSelector(
    (state: State) => getClientLibrary(state, libraryId),
  )
  const easyAccess = library?.discovery_access_level === 'discovery_easy_access'
  const clientLibrariesTrial: boolean = useSelector(isClientLibrariesTrial)
  useEffect(() => {
    dispatch(clientLibraryListGetSingle(libraryId))
  }, [dispatch, libraryId])
  const albumsEnabled: boolean = useFeatureEnabled(ALBUMS)
  const liveLibrariesEnabled: boolean =
    useFeatureEnabled(LIVE_LIBRARIES) && !clientLibrariesTrial

  const handleQRAction = useCallback((action: 'download' | 'copy') => {
    mixpanel.sendPureEvent('Catalog QR code interaction', {
      Action: action,
    })
  }, [])

  const toggleIsLive = (value: boolean) => {
    dispatch(
      clientLibraryUpdate(libraryId, {
        is_live: value,
      }),
    )
  }

  const typeData = `${useComposerInsteadOfArtist().title}s`
  const NAV_ITEMS = useMemo(
    () => [
      {
        label: 'Content',
        expandable: true,
        items: [
          {
            label: 'Connect channels',
            link: `/catalogs/${libraryId}/content`,
          },
          {
            label: 'Manage tracks',
            link: `/catalogs/${libraryId}/tracks`,
          },
          {
            label: 'Manage playlists',
            link: `/catalogs/${libraryId}/playlists`,
          },
          albumsEnabled && {
            label: 'Manage albums',
            link: `/catalogs/${libraryId}/albums`,
          },
          {
            label: `Manage ${typeData.toLowerCase()}`,
            link: `/catalogs/${libraryId}/artists`,
          },
        ].filter(Boolean),
      },
      {
        label: 'Design',
        expandable: true,
        items: [
          {
            label: 'Catalog design',
            link: `/catalogs/${libraryId}/design`,
          },
          {
            label: 'Contact page',
            link: `/catalogs/${libraryId}/contact`,
          },
        ],
      },
      {
        label: 'Clients & Promotion',
        expandable: true,
        items: [
          {
            label: 'Access & promotion',
            link: `/catalogs/${libraryId}/access`,
          },
          {
            label: 'Clients',
            link: `/catalogs/${libraryId}/clients`,
          },
          {
            label: 'Stats',
            link: `/catalogs/${libraryId}/stats`,
          },
        ],
      },
      {
        label: 'Settings & Notifications',
        expandable: true,
        items: [
          {
            label: 'General settings',
            link: `/catalogs/${libraryId}/settings`,
          },
          {
            label: 'Notifications',
            link: `/catalogs/${libraryId}/notifications`,
          },
        ],
      },
    ],
    [albumsEnabled, libraryId, typeData],
  )
  const showEasyAccess = easyAccess && location.pathname.includes('/clients')
  // Show only easy access alert on clients page
  const hideAlerts = showEasyAccess || Boolean(library?.loading)
  const libraryAlert =
    easyAccess && location.pathname.includes('/stats') ? (
      <LibraryEasyAccessAlert />
    ) : null
  const title = (
    <Flex>
      <span translate="no">{library?.name}</span>
      {Boolean(library?.primary) && <Lozenge text="Primary" color="blue" />}
    </Flex>
  )
  return location.state?.modal ? (
    <Outlet />
  ) : (
    <div className="Page">
      <div className="Page_Content">
        <PageLayout title="Catalog settings" nav={NAV_ITEMS}>
          <PageHeader
            breadcrumbs={{
              content: 'Catalogs',
              link: '/catalogs',
            }}
            title={library && title}
            actions={
              <ButtonContainer className="flc">
                <span className={styles.libraryLiveLabel}>
                  {library?.is_live && liveLibrariesEnabled
                    ? 'Live'
                    : 'Unpublished'}
                </span>
                <Switch
                  disabled={
                    !liveLibrariesEnabled || !library || library.loading
                  }
                  value={liveLibrariesEnabled && Boolean(library?.is_live)}
                  onChange={toggleIsLive}
                />
                {!library?.public_url ? null : (
                  <Button
                    caption="View Catalog"
                    link={library.public_url}
                    linkTarget="_blank"
                  />
                )}
                {!library?.public_url ? null : (
                  <QRCodeButton
                    url={library.public_url}
                    filename={`${library.name || 'catalog'}-qr`}
                    onAction={handleQRAction}
                  />
                )}
                {!library?.public_url ? null : (
                  <CopyToClipboard
                    component="div"
                    data-clipboard-text={library.public_url}
                    onSuccess={() =>
                      pushUINotification({
                        message: 'Copied!',
                        level: 'success',
                      })
                    }
                  >
                    <Button icon="link" tooltip="Copy URL" />
                  </CopyToClipboard>
                )}
              </ButtonContainer>
            }
          >
            {liveLibrariesEnabled
              ? !hideAlerts && libraryAlert
              : !hideAlerts && (
                  <LibrariesUpgradeAlert
                    clientLibrariesTrial={clientLibrariesTrial}
                  />
                )}

            {showEasyAccess && (
              <div className={styles.libraryAlertContainer}>
                <Alert icon="info" status="info">
                  Your library is configured to be visible to over 800 vetted
                  professional music supervisors. Once they engage in your
                  library by streaming or downloading tracks, they will appear
                  here as clients.{' '}
                  <a
                    className="text-link"
                    href="https://support.disco.ac/home/catalogs-guide#enabling-easy-access-to-make-your-catalog-discoverable-by-music-supervisors-on-disco"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Find out more.
                  </a>
                </Alert>
              </div>
            )}
          </PageHeader>
          <PageContainer>
            <Outlet />
          </PageContainer>
        </PageLayout>
      </div>
    </div>
  )
}

export default LibrariesLibrary
